import React, { useEffect, useState } from 'react';
import { getAccessToken } from '../services/auth';

// Partner links: people we pay per sale for sending us customers (Greg, 2026-10-01; first
// one is Alec at 12%). One row per partner from netlify/functions/lib/partner-links.js,
// with clicks on their crabplace.co link, the orders that arrived on it, and commission
// owed. Link-only, no code, so this counts last-click same-browser orders: a floor.
// Adding a partner is a row in that file plus a Bitly link; nothing changes here.

const money = (n) => (n == null ? '—' : '$' + Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
const th = { textAlign: 'right', fontSize: 11, fontWeight: 700, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: 0.4, padding: '8px 10px', borderBottom: '1px solid var(--gray-200)' };
const td = { textAlign: 'right', fontSize: 13, color: 'var(--navy)', padding: '10px', borderBottom: '1px solid var(--gray-100)' };

export default function PartnerLinks() {
  const [partners, setPartners] = useState(null);
  const [error, setError] = useState(null);
  const [open, setOpen] = useState(null);

  useEffect(() => {
    const token = getAccessToken();
    fetch('/.netlify/functions/partner-links', { headers: token ? { Authorization: `Bearer ${token}` } : {} })
      .then(async (r) => { const d = await r.json(); if (!r.ok) throw new Error(d.error || `Error ${r.status}`); return d; })
      .then((d) => setPartners(d.partners || []))
      .catch((e) => setError(e.message));
  }, []);

  return (
    <div style={{ background: '#fff', border: '1px solid var(--gray-200)', borderRadius: 10, padding: 20, marginTop: 16 }}>
      <h3 style={{ margin: 0, color: 'var(--navy)', fontSize: 16 }}>Partner links</h3>
      <p style={{ margin: '6px 0 14px', fontSize: 13, color: 'var(--gray-500)', lineHeight: 1.5 }}>
        People we pay per sale. An order counts when the customer clicked the partner&rsquo;s link and ordered in the same browser,
        so these numbers are a floor. Commission is on product sales, not shipping or tax.
      </p>
      {error && <div style={{ color: '#b42318', fontSize: 13 }}>Could not load partner links: {error}</div>}
      {!error && !partners && <div style={{ color: 'var(--gray-500)', fontSize: 13 }}>Loading&hellip;</div>}
      {partners && (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 640 }}>
            <thead>
              <tr>
                <th style={{ ...th, textAlign: 'left' }}>Partner</th>
                <th style={{ ...th, textAlign: 'left' }}>Link</th>
                <th style={th}>Clicks</th>
                <th style={th}>Orders</th>
                <th style={th}>New customers</th>
                <th style={th}>Product sales</th>
                <th style={th}>Commission</th>
              </tr>
            </thead>
            <tbody>
              {partners.map((p) => (
                <React.Fragment key={p.slug}>
                  <tr onClick={() => setOpen(open === p.slug ? null : p.slug)} style={{ cursor: p.orderList.length ? 'pointer' : 'default' }}>
                    <td style={{ ...td, textAlign: 'left', fontWeight: 700 }}>
                      {p.name}
                      <div style={{ fontSize: 11, fontWeight: 400, color: 'var(--gray-500)' }}>{p.commissionPct}% per sale &middot; since {p.startedOn}</div>
                    </td>
                    <td style={{ ...td, textAlign: 'left' }}><a href={p.shortUrl} target="_blank" rel="noopener noreferrer">{p.shortUrl.replace('https://', '')}</a></td>
                    <td style={td}>{p.clicks == null ? '—' : p.clicks.toLocaleString()}</td>
                    <td style={td}>{p.orders}{p.pendingOrders ? ` (${p.pendingOrders} pending)` : ''}</td>
                    <td style={td}>{p.newCustomers}</td>
                    <td style={td}>{money(p.productSales)}</td>
                    <td style={{ ...td, fontWeight: 700 }}>{money(p.commission)}</td>
                  </tr>
                  {open === p.slug && p.orderList.map((o) => (
                    <tr key={o.bcOrderId} style={{ background: 'var(--gray-50, #f8f9fb)' }}>
                      <td style={{ ...td, textAlign: 'left', fontSize: 12 }} colSpan={2}>
                        Order {o.bcOrderId}{o.customer ? ` · ${o.customer}` : ''} &middot; {new Date(o.placedAt).toLocaleDateString('en-US')}
                        {o.void ? ' · canceled' : ''}{o.pending ? ' · amount pending' : ''}
                      </td>
                      <td style={td} />
                      <td style={{ ...td, fontSize: 12 }}>{money(o.orderTotal)}</td>
                      <td style={{ ...td, fontSize: 12 }}>{o.newCustomer ? 'New' : 'Returning'}</td>
                      <td style={{ ...td, fontSize: 12 }}>{money(o.productSales)}</td>
                      <td style={{ ...td, fontSize: 12 }}>{money(o.commission)}</td>
                    </tr>
                  ))}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
